import React, { useEffect, useMemo, useRef } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { CheckCircle } from 'lucide-react';
import { Button } from '@ui/button';
import { Badge } from '@ui/badge';
import { UserHoverCard } from '@/components/common/UserHoverCard';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { useMarkNotificationsAsRead } from '@/hooks/queries/notification';
import { useInboxSession } from '@/components/notifications/InboxSessionContext';
import { useNotificationMentionUsers } from '@/hooks/useNotificationMentionUsers';
import { cn } from '@/lib/utils';
import type { UserNotificationDetails } from '@/types/notifications';
import {
  formatFullDateTime,
  formatGermanRelativeTime,
  getFormattedLabel,
  getNotificationBodyPreview,
  getNotificationDisplayTimestamp,
  getNotificationTypeInfo,
  getNotificationUserDisplayName,
  getNotificationUserInitials,
  processInboxNotificationAction,
  toNotificationUserHoverDetails,
} from '@/lib/notificationUtils';
// import { useGetFolderById } from '@/hooks/queries/folder';

interface NotificationItemProps {
  notification: UserNotificationDetails['notifications'][number];
}

/**
 * Format timestamp to relative time (for V1)
 */
function formatRelativeTime(timestamp: number): string {
  const date = new Date(timestamp);
  const now = new Date();
  const diffInSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);
  
  if (diffInSeconds < 60) return 'Just now';
  if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
  if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
  if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)}d ago`;
  return date.toLocaleDateString('en-US', { day: 'numeric', month: 'short' });
}

/**
 * Handle notification click
 */
function handleNotificationClick(
  notification: UserNotificationDetails['notifications'][number],
  markAsReadMutation: ReturnType<typeof useMarkNotificationsAsRead>,
  navigate: ReturnType<typeof useNavigate>,
  acknowledgeNotification: (notificationId: number) => void = () => undefined,
) {
  processInboxNotificationAction(notification, {
    navigate,
    acknowledgeNotification,
    markAsRead: (params) => markAsReadMutation.mutate(params),
  });
}

/**
 * Handle mark as read button click
 */
function handleMarkAsReadClick(
  e: React.MouseEvent,
  notificationId: number,
  markAsReadMutation: ReturnType<typeof useMarkNotificationsAsRead>,
  groupId: number
) {
  e.stopPropagation();
  markAsReadMutation.mutate({
    notificationIds: [notificationId],
    groupId
  });
}

/**
 * Notification item component - Version 1
 */
export function NotificationItemV1({ notification }: NotificationItemProps) {
  const navigate = useNavigate();
  const markAsReadMutation = useMarkNotificationsAsRead();
  const mentionUsers = useNotificationMentionUsers();
  const typeInfo = getNotificationTypeInfo(notification.notificationType);
  const label = useMemo(() => getFormattedLabel(typeInfo), [typeInfo]);
  const senderName = useMemo(
    () => getNotificationUserDisplayName(notification.senderUser),
    [notification.senderUser]
  );
  const bodyPreview = useMemo(
    () =>
      getNotificationBodyPreview(
        notification.notificationBody,
        notification.senderUser,
        mentionUsers,
      ),
    [mentionUsers, notification.notificationBody, notification.senderUser]
  );
  
  return (
    <div 
      className={`group flex cursor-pointer items-start space-x-3 px-4 py-4 transition-all duration-200 rounded-lg mx-2 mb-2 border ${
        'bg-white hover:bg-gray-50 border-gray-200' 
        // notification.isRead 
          // ? 'bg-white hover:bg-gray-50 border-gray-200' 
          // : 'bg-slate-50 hover:bg-slate-100 border-slate-200 shadow-sm'
      }`}
      onClick={() => handleNotificationClick(notification, markAsReadMutation, navigate)}
    >
      {/* Avatar */}
      <div className="flex-shrink-0">
        <UserHoverCard
          user={toNotificationUserHoverDetails(notification.senderUser)}
          triggerClassName="font-medium text-blue-600 group-hover:text-blue-800 transition-colors duration-200"
        >
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100 transition-colors duration-200 group-hover:bg-blue-300">
            <span className="text-sm font-medium text-blue-600 transition-colors duration-200 group-hover:text-blue-800">
              {getNotificationUserInitials(notification.senderUser)}
            </span>
          </div>
        </UserHoverCard>
      </div>

      {/* Content */}
      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className={`text-sm transition-colors duration-200 ${
              // notification.isRead ? 'text-gray-500' : 'text-gray-900'
              'text-gray-500'
            }`}>
              <UserHoverCard 
                user={toNotificationUserHoverDetails(notification.senderUser)}
                triggerClassName="font-medium transition-colors duration-200 text-blue-500 group-hover:text-blue-700"
              >
                {senderName}
              </UserHoverCard>
              {' '}{bodyPreview}
            </p>
            <div className="mt-2 flex items-center space-x-2">
              <div className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium transition-colors duration-200 ${
                'bg-gray-100 text-gray-500 group-hover:bg-gray-200'
                // notification.isRead 
                //   ? 'bg-gray-100 text-gray-500 group-hover:bg-gray-200' 
                //   : 'bg-slate-100 text-slate-700 group-hover:bg-slate-200'
              }`}>
                {notification.listItemType}
              </div>
              <div className={`inline-flex items-center rounded-full px-2 py-1 text-xs font-medium transition-colors duration-200 ${
                notification.isRead 
                  ? 'bg-gray-100 text-gray-500 group-hover:bg-gray-200' 
                  : typeInfo.color
              }`}>
                {label}
              </div>
            </div>
          </div>

          {/* Timestamp and Mark as Read Button */}
          <div className="ml-4 flex-shrink-0 flex flex-col items-end space-y-1">
            <span className={`text-xs transition-colors duration-200 ${
              notification.isRead ? 'text-gray-400' : 'text-gray-600'
            }`}>
              {formatRelativeTime(getNotificationDisplayTimestamp(notification))}
            </span>
            {/* {!notification.isRead && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => handleMarkAsReadClick(e, notification.id, markAsReadMutation, notification.groupId)}
                disabled={markAsReadMutation.isPending}
                className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-all duration-200"
              >
                <CheckCircle className="mr-1 h-3 w-3" />
                Mark Read
              </Button>
            )} */}
          </div>
        </div>
      </div>

      {/* Read indicator */}
      {/* {!notification.isRead && (
        <div className="flex-shrink-0">
          <div className="h-2 w-2 rounded-full bg-slate-500 transition-colors duration-200 group-hover:bg-slate-600"></div>
        </div>
      )} */}
    </div>
  );
}

/**
 * Notification item component - Version 2
 */
export function NotificationItemV2({ notification }: NotificationItemProps) {
  const navigate = useNavigate();
  const markAsReadMutation = useMarkNotificationsAsRead();
  const mentionUsers = useNotificationMentionUsers();
  const typeInfo = getNotificationTypeInfo(notification.notificationType);
  const label = useMemo(() => getFormattedLabel(typeInfo), [typeInfo]);
  const senderName = useMemo(
    () => getNotificationUserDisplayName(notification.senderUser),
    [notification.senderUser]
  );
  const bodyPreview = useMemo(
    () =>
      getNotificationBodyPreview(
        notification.notificationBody,
        notification.senderUser,
        mentionUsers,
      ),
    [mentionUsers, notification.notificationBody, notification.senderUser]
  );

  return (
    <div 
      className={`flex items-start space-x-4 py-4 px-6 transition-all duration-200 cursor-pointer group rounded-lg border ${
        notification.isRead 
          ? 'bg-white hover:bg-gray-50 border-gray-200' 
          : 'bg-slate-50 hover:bg-slate-100 border-slate-200 shadow-sm'
      }`}
      onClick={() => handleNotificationClick(notification, markAsReadMutation, navigate)}
    >
      {/* Read/Unread indicator */}
      {/* <div className="flex-shrink-0 mt-1">
        <div className={cn("w-2 h-2 bg-blue-500 rounded-full", notification.isRead ? 'opacity-0' : 'opacity-100')} />
      </div> */}
      
      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Badge and timestamp row */}
        <div className="flex items-center justify-between mb-2">
          <Badge 
            variant="outline" 
            className={cn('text-xs font-medium', 
              typeInfo.color,
              notification.isRead ? 'opacity-50' : 'opacity-100'
            )}
          >
            <typeInfo.icon className="mr-1 h-3 w-3" />
            <span className="ml-1">{label}</span>
          </Badge>
          <div className="flex items-center space-x-2">
            <span className={`text-xs font-medium ${
              notification.isRead ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {formatFullDateTime(getNotificationDisplayTimestamp(notification))}
            </span>
            {!notification.isRead && (
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => handleMarkAsReadClick(e, notification.id, markAsReadMutation, notification?.groupId)}
                disabled={markAsReadMutation.isPending}
                className="h-6 px-2 text-xs text-blue-600 hover:text-blue-800 hover:bg-blue-50 transition-all duration-200"
              >
                <CheckCircle className="mr-1 h-3 w-3" />
                Mark Read
              </Button>
            )}
          </div>
        </div>
        
        {/* Main notification content */}
        <div className="space-y-1">
          <p className={`text-sm leading-relaxed ${
            notification.isRead ? 'text-gray-600' : 'text-gray-900'
          }`}>
            <UserHoverCard 
              user={toNotificationUserHoverDetails(notification.senderUser)}
              triggerClassName={`font-semibold transition-colors duration-200 ${
                notification.isRead 
                  ? 'text-blue-500 hover:text-blue-700' 
                  : 'text-blue-600 hover:text-blue-700'
              }`}
            >
              {senderName}
            </UserHoverCard>
            {' '}{bodyPreview}
          </p>
          
          {/* Additional details - only show if there's extra info */}
          {(notification.notificationTitle || notification.listItemType !== 'FOLDER') && (
            <div className={`text-xs mt-1 ${
              notification.isRead ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {notification.notificationTitle && (
                <span className="block">{notification.notificationTitle}</span>
              )}
              <span className="text-gray-300">•</span>
              <span className="ml-1">{notification.listItemType}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * NotificationItemV3
 *
 * Renders a single notification in the V3 style.
 * - Shows icon, label, timestamp, sender, type, and message.
 * - Highlights unread notifications.
 * - Clicking marks as read and navigates if needed.
 *
 * Props:
 *   - notification: UserNotificationDetails['notifications'][number]
 */
export function NotificationItemV3({ notification }: NotificationItemProps) {
  // Navigation and mutation hooks
  const navigate = useNavigate();
  const markAsReadMutation = useMarkNotificationsAsRead();
  const mentionUsers = useNotificationMentionUsers();

  // Get notification type info (icon, color, label)
  const typeInfo = getNotificationTypeInfo(notification.notificationType);

  // Memoized label and sender full name
  // const label = useMemo<string>(() => getFormattedLabel(typeInfo), [typeInfo]);
  const label = useMemo<string>(() => notification.notificationTitle, [notification]);
  const fullName = useMemo<string>(
    () => getNotificationUserDisplayName(notification.senderUser),
    [notification.senderUser]
  );
  const bodyPreview = useMemo(
    () =>
      getNotificationBodyPreview(
        notification.notificationBody,
        notification.senderUser,
        mentionUsers,
      ),
    [mentionUsers, notification.notificationBody, notification.senderUser]
  );

  // const { data: itemDetails } = useGetFolderById(notification.listItemId, '1');
  // const { data: parentDetails } = useGetFolderById(notification?.parentId || -1, '1');

  // const path = useMemo<string[]>(
  //   () => {
  //     console.log(parentDetails?.data?.title, itemDetails?.data?.title);
  //     const parentName = parentDetails?.data.title || String(parentDetails?.data.id) === '-1' ? 'Root' : '';
  //     const itemName = itemDetails?.data.title || '';
  //     return [parentName, itemName].filter(Boolean);
  //   },
  //   [parentDetails, itemDetails]
  // );

  // Handle notification click: mark as read and navigate if needed
  function handleClick(): void {
    handleNotificationClick(notification, markAsReadMutation, navigate);
  }

  return (
    <div
      className={cn(
        "relative flex items-start px-3 py-2.5 cursor-pointer border-b border-gray-200",
        "transition-colors",
        notification.isRead
          ? "hover:bg-gray-50"
          : "bg-orange-50/50 hover:bg-orange-50"
      )}
      onClick={handleClick}
      data-testid={`notification-item-${notification.id}`}
    >
      {/* Left vertical indicator for unread */}
      <div className="absolute left-0 top-0 h-full">
        <div
          className={cn(
            "w-0.5 h-full rounded-full",
            notification.isRead ? "bg-transparent" : "bg-orange-400"
          )}
        />
      </div>

      {/* Main content */}
      <div className="flex-1 min-w-0">
        {/* Header: Icon, label, unread dot, timestamp */}
        <div className="flex items-center justify-between mb-1">
          <div className="flex items-center gap-2">
            <typeInfo.icon className="h-4 w-4 text-gray-600 flex-shrink-0" />
            <span className="text-sm font-semibold text-gray-900 truncate">{label}</span>
            {!notification.isRead && (
              <div className="w-1.5 h-1.5 bg-orange-400 rounded-full" />
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            <span
              className={cn(
                "text-xs",
                notification.isRead ? "text-gray-500" : "text-orange-500"
              )}
            >
              {formatFullDateTime(getNotificationDisplayTimestamp(notification))}
            </span>
            {!notification.isRead && (
              <span className="text-xs text-orange-300">●</span>
            )}
          </div>
        </div>

        {/* Sender */}
        <div className="text-xs text-gray-500 mb-1 font-medium">
          von {fullName}
        </div>

        {/* Category/Tags */}
        {/* <div className="text-xs text-gray-500 mb-1">
          {path.join(' > ')}
        </div> */}

        {/* Message */}
        <div className="text-xs text-gray-700 font-medium line-clamp-2">
          {bodyPreview}
        </div>
      </div>
    </div>
  );
}

/**
 * Parse notification body to extract and style mentions
 * Mentions are typically full names in the format "FirstName LastName"
 */
function parseNotificationBody(
  body: string
): Array<{ type: 'text' | 'mention'; content: string }> {
  const text = body;
  
  // Simple regex to find potential mentions (capitalized words that might be names)
  // This is a basic implementation - can be improved with actual user list
  const parts: Array<{ type: 'text' | 'mention'; content: string }> = [];
  const mentionRegex = /\b([A-Z][a-z]+ [A-Z][a-z]+)\b/g;
  let lastIndex = 0;
  let match;
  
  while ((match = mentionRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push({
        type: 'text',
        content: text.slice(lastIndex, match.index),
      });
    }
    
    // Add mention
    parts.push({
      type: 'mention',
      content: match[1],
    });
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text
  if (lastIndex < text.length) {
    parts.push({
      type: 'text',
      content: text.slice(lastIndex),
    });
  }
  
  // If no mentions found, return the whole text as a single part
  if (parts.length === 0) {
    parts.push({ type: 'text', content: text });
  }
  
  return parts;
}

/**
 * NotificationItemV4
 *
 * Renders a single notification in the V4 style matching the Figma design.
 * - Shows profile picture, sender name, context, timestamp, and message preview
 * - Highlights unread notifications with orange/yellow dot
 * - Clicking marks as read and navigates if needed
 * - Parses and styles mentions in blue
 *
 * Props:
 *   - notification: UserNotificationDetails['notifications'][number]
 */
export function NotificationItemV4({ notification }: NotificationItemProps) {
  // Navigation and mutation hooks
  const navigate = useNavigate();
  const markAsReadMutation = useMarkNotificationsAsRead();
  const { acknowledgeNotification, acknowledgedNotificationIds, nextUnreadCursorId } =
    useInboxSession();
  const mentionUsers = useNotificationMentionUsers();

  // Memoized sender full name
  const fullName = useMemo<string>(
    () => getNotificationUserDisplayName(notification.senderUser),
    [notification.senderUser]
  );

  const initials = useMemo<string>(
    () => getNotificationUserInitials(notification.senderUser),
    [notification.senderUser]
  );

  const formattedTime = useMemo<string>(
    () => formatGermanRelativeTime(getNotificationDisplayTimestamp(notification)),
    [notification]
  );

  // Memoized notification context (e.g., "MülltonnenInstandhalt...")
  const notificationContext = useMemo<string>(() => {
    return notification.notificationTitle || '';
  }, [notification.notificationTitle]);

  // Memoized parsed message body with mentions
  const messageParts = useMemo(() => {
    const preview = getNotificationBodyPreview(
      notification.notificationBody,
      notification.senderUser,
      mentionUsers,
    );
    return parseNotificationBody(preview);
  }, [mentionUsers, notification.notificationBody, notification.senderUser]);

  const isAcknowledgedInSession = acknowledgedNotificationIds.has(notification.id);
  const showUnreadIndicator = !notification.isRead && !isAcknowledgedInSession;
  const isNextUnreadHighlight = nextUnreadCursorId === notification.id;
  const itemRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isNextUnreadHighlight || !itemRef.current) {
      return;
    }

    itemRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [isNextUnreadHighlight]);

  // Handle notification click: mark as read and navigate if needed
  function handleClick(): void {
    handleNotificationClick(
      notification,
      markAsReadMutation,
      navigate,
      acknowledgeNotification,
    );
  }

  return (
    <div
      ref={itemRef}
      className={cn(
        'flex items-start gap-3 border-l-2 py-3 pr-4 cursor-pointer transition-colors',
        isNextUnreadHighlight
          ? 'border-l-blue-500 bg-blue-50 ring-1 ring-inset ring-blue-200'
          : 'border-l-transparent hover:bg-gray-50',
      )}
      onClick={handleClick}
      data-testid={`notification-item-v4-${notification.id}`}
      data-next-unread-highlight={isNextUnreadHighlight ? 'true' : undefined}
    >
      {/* Profile Picture */}
      <Avatar className="h-8 w-8 shrink-0 rounded-full">
        <AvatarFallback className="bg-gray-200 text-gray-700 text-xs font-medium rounded-md p-1">
          {initials}
        </AvatarFallback>
      </Avatar>

      {/* Content */}
      <div className="flex-1 min-w-0">
        {/* Header: User name, context, timestamp, and unread dot */}
        <div className="flex items-center justify-between gap-2 mb-1">
          <div className="flex items-center gap-1.5 min-w-0 flex-1">
            <UserHoverCard
              user={toNotificationUserHoverDetails(notification.senderUser)}
              triggerClassName="text-xs font-semibold text-gray-900 hover:text-blue-600 transition-colors"
            >
              {fullName}
            </UserHoverCard>
            <span className="text-xs text-gray-600">in</span>
            <span className="text-xs text-gray-900 underline truncate">
              {notificationContext}
            </span>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-xs text-gray-500">{formattedTime}</span>
            {showUnreadIndicator ? (
              <div className="w-2 h-2 bg-orange-400 rounded-full shrink-0" />
            ) : null}
          </div>
        </div>

        {/* Message Preview with Mentions */}
        <div className="text-xs text-gray-700 line-clamp-2">
          {messageParts.map((part, index) => {
            if (part.type === 'mention') {
              return (
                <span key={index} className="font-semibold text-blue-600">
                  {part.content}
                </span>
              );
            }
            return <span key={index}>{part.content}</span>;
          })}
        </div>
      </div>
    </div>
  );
}

